import { AppData, DocumentServicesObject } from '@wix/document-services-types';
import { IntentInstallOptions } from '@wix/editor-platform-host-integration-apis';
import {
  PlatformApiMethods,
  PlatformContext,
} from '../../../../types/platformApi';
import { IExperimentsApiAdapter } from '../../../utils/experimentsApiAdapter';
import * as unifiedComponents from '../../unifiedComponents/installation/addApps';
import { IntentInstallStore } from '../IntentInstallStore';
import type { ProgressBarManager } from '../ProgressBarManager';
import { ADD_APP_ERR, BEFORE_INSTALL_APP_HISTORY_LABEL } from '../constants';
import { IntentInstallErrorCode, createIntentInstallError } from '../errors';
import { createPromiseWithResolvers, getCommonAppOptions } from './helpers';
import { InstallationStrategy } from './types';
import { getPluginsEditorType } from '../../../../utils';
import {
  autoInstallWithNavigation,
  autoInstall,
} from '../../plugins/pluginAutoPopulateService';
import { getAllPluginsInstallationParams } from '../../slotsService';

type BaseInstallationStrategyOptions = {
  shouldUpdateProgressBar?: boolean;
};

export class BaseInstallationStrategy
  implements InstallationStrategy<BaseInstallationStrategyOptions>
{
  constructor(
    private platformApiMethods: PlatformApiMethods,
    private documentServicesAPI: DocumentServicesObject,
    private experimentsAPI: IExperimentsApiAdapter,
    private intentInstallStore: IntentInstallStore,
    private progressBarManager: ProgressBarManager,
  ) {}

  private async autoPopulateWidgetPlugins(
    context: PlatformContext,
    appDefinitionId: string,
    appData: AppData,
  ) {
    const pluginInstallationParams = await getAllPluginsInstallationParams(
      this.documentServicesAPI,
      appDefinitionId,
      appData?.components || [],
    );

    if (!pluginInstallationParams.length) {
      return;
    }

    const editorType = getPluginsEditorType();
    const originType = this.intentInstallStore.getOriginType();

    for (const [index, params] of pluginInstallationParams.entries()) {
      if (index === pluginInstallationParams.length - 1) {
        await autoInstallWithNavigation(
          context,
          this.documentServicesAPI,
          params,
          {
            origin: originType,
            editorType,
          },
          context.experimentsAPI,
        );
      } else {
        await autoInstall(
          context,
          this.documentServicesAPI,
          params,
          {
            origin: originType,
            editorType,
          },
          context.experimentsAPI,
        );
      }
    }
  }

  private getErrorHandler(
    context: PlatformContext,
    strategyOptions?: BaseInstallationStrategyOptions,
  ) {
    return async (
      error: Error,
      errorName: string,
      rawAppDefinitionId: string,
    ) => {
      let appDefinitionId = rawAppDefinitionId;

      try {
        appDefinitionId = JSON.parse(rawAppDefinitionId)?.[0];
      } catch {}

      this.intentInstallStore.addAppWithError(appDefinitionId);

      console.error(
        createIntentInstallError(
          IntentInstallErrorCode.UnifiedInstallationError,
          errorName,
        )
          .withParentError(error)
          .withAppDefinitionId(appDefinitionId),
      );

      let errorDesc;
      try {
        errorDesc = error?.message || JSON.stringify(error);
      } catch (e) {
        errorDesc = 'cannot stringify error from Document Management';
      }

      // TODO(intent-install): add logging

      if (errorName === ADD_APP_ERR) {
        await context.platformApiMethods.document.history.performUndoUntilLabel(
          {},
          '',
          { snapshotLabel: BEFORE_INSTALL_APP_HISTORY_LABEL + appDefinitionId },
        );
      }

      if (strategyOptions?.shouldUpdateProgressBar) {
        this.progressBarManager.startFakeProgressBarForNextApp();
      }
    };
  }

  public async install(
    context: PlatformContext,
    apps: string[],
    options: IntentInstallOptions,
    strategyOptions?: BaseInstallationStrategyOptions,
  ): Promise<void> {
    const currentInstallations: Promise<void>[] = [];

    return new Promise<void>((resolve) => {
      unifiedComponents
        .installApps(
          context,
          this.documentServicesAPI,
          this.experimentsAPI,
          apps,
          {
            noForcedSaveAfterInstallationCompleted: true, // there is custom save at the end of intent install
            appsOptions: getCommonAppOptions(
              this.intentInstallStore,
              apps,
              options,
              this.experimentsAPI,
            ),
          },
          {
            singleAppCallback: async (appDefinitionId, appData) => {
              if (
                this.experimentsAPI.enabled(
                  'se_autoPopulatePluginPartOfInstallation',
                ) ||
                this.experimentsAPI.enabled(
                  'specs.responsive-editor.autoPopulatePluginPartOfInstallation',
                )
              ) {
                // AutoPopulate will be called in the end of the installation
                return;
              }
              const currentInstallationPromise =
                createPromiseWithResolvers<void>();
              currentInstallations.push(currentInstallationPromise.promise);

              try {
                await this.autoPopulateWidgetPlugins(
                  context,
                  appDefinitionId,
                  appData,
                );
              } catch (e) {
                console.error(
                  createIntentInstallError(
                    IntentInstallErrorCode.AutoPopulationFailed,
                  )
                    .withAppDefinitionId(appDefinitionId)
                    .withParentError(e as Error),
                );
              } finally {
                if (strategyOptions?.shouldUpdateProgressBar) {
                  this.progressBarManager.startFakeProgressBarForNextApp();
                }

                currentInstallationPromise.resolve();
              }
            },
            finishAllCallback: async () => {
              // so installation is not finishes before all apps post installs are done (plugins population)
              // so we keep progress bar open
              await Promise.allSettled(currentInstallations);

              resolve();
            },
          },
        )
        .catch((e) => {
          console.error(e);
          resolve();
        });
    });
  }
}
